import { type FormEvent, useState } from 'react'
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import PasswordInput from '@/components/auth/PasswordInput'
import { useSignInMutation } from '@/hooks/useAuthActions'

const SignIn = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const signIn = useSignInMutation()
    const [values, setValues] = useState({
        email: '',
        password: '',
    })

    const state =
        (location.state as {
            from?: Location
            passwordReset?: boolean
        } | null) ?? null
    const fromPath = state?.from?.pathname ?? '/'
    const passwordReset = state?.passwordReset

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        signIn.mutate(
            {
                email: values.email,
                password: values.password,
            },
            {
                onSuccess: () => {
                    navigate(fromPath, { replace: true })
                },
            }
        )
    }

    return (
        <div className="space-y-8">
            {passwordReset ? (
                <div className="rounded-2xl bg-[#E1F6F8] px-4 py-3 text-sm text-[#0C1324]">
                    Your password has been updated. Sign in with your new
                    credentials.
                </div>
            ) : null}
            <div className="space-y-2">
                <h1 className="text-[22px] font-semibold text-[#0C1324]">
                    Sign in
                </h1>
                <p className="text-sm text-sidebar-foreground">
                    Enter your details to manage operations and oversee platform
                    activity.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-4">
                    <Input
                        type="email"
                        placeholder="Enter email"
                        className="rounded-[12px]"
                        value={values.email}
                        onChange={event =>
                            setValues(prev => ({
                                ...prev,
                                email: event.target.value,
                            }))
                        }
                        required
                    />

                    <PasswordInput
                        placeholder="Password"
                        value={values.password}
                        className="rounded-[12px]"
                        onChange={event =>
                            setValues(prev => ({
                                ...prev,
                                password: event.target.value,
                            }))
                        }
                        required
                    />
                </div>

                <div className="">
                    <Link
                        to="/auth/forgot-password"
                        className="text-sm font-medium text-[#717784] hover:underline"
                    >
                        Forgot password?
                    </Link>
                </div>

                {signIn.isError ? (
                    <p className="text-sm text-destructive">
                        {(signIn.error as Error)?.message ??
                            'Unable to sign in with those credentials.'}
                    </p>
                ) : null}

                <Button
                    type="submit"
                    disabled={signIn.isPending}
                    className="h-12 w-full rounded-full bg-[#0FA3B1] text-base font-semibold hover:bg-[#0C8D98]"
                >
                    {signIn.isPending ? 'Signing in...' : 'Sign in'}
                </Button>
            </form>
        </div>
    )
}

export default SignIn
